/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { DataMap, HeadersVisibility } from '@grapecity/wijmo.grid';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox } from 'components/selects/ComboBox';
import { CommonUtil } from 'utils/CommonUtil';
import CustomChip from 'components/selects/CustomChip';
import { Code, commonYNList } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { checkAvailableProjectManager } from 'apis/setup/SetupProject';
import { hasRole } from 'utils/SessionUtil';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import {
  findActivityCandidates,
  findOrgEqclGroups,
  findPrjActivities,
  generatePrjWo,
  savePrjActivities,
} from 'apis/setup/SetupActivity';
import { EqclGroup, PrjActivity, PrjActivityCondition } from 'models/setup/SetupActivity';
import { SetupStep } from 'models/setup/SetupStep';
import { findSetupStep } from 'apis/setup/SetupStep';
import { IconButton } from 'components/buttons/IconSVG';
import { CrudCode } from 'models/common/Edit';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { findDisplayProject } from 'apis/setup/SetupProject';
import { SetupProject } from 'models/setup/SetupProject';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 프로젝트별 Activity 조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.20   jshwang12       신규 생성
 * 2026.09.01   jshwang12       권한 설정  (FA 관리자, 시스템관리자, 해당 프로젝트의 FA 담당자(본사/법인))
 */
const SetupProjectActivityPage = () => {
  const gridRef = useRef<any>();
  const conditionRef = useRef<any>();
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const defaultCondition = {
    elmPrjId: '',
    orgTpCd: '',
    istlDivsCd: '',
    prjSpCd: '',
  };
  const [condition, setCondition] = useState<PrjActivityCondition>({ ...defaultCondition });
  const [errors, setErrors] = useState<any>({});
  const [rowData, setRowData] = useState<PrjActivity[]>([]);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]); //프로젝트
  const [prjSpCds, setPrjSpCds] = useState<SetupStep[]>([]);
  const [code, setCode] = useState<any>();
  const [selectedEqclIds, setSelectedEqclIds] = useState<string[]>(['ALL']);
  const [eqclGroups, setEqclGroups] = useState<EqclGroup[]>([]);
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const optionsEqclGroups = useMemo(() => {
    return [
      { cmnCd: 'ALL', cmnCdNm: String(t('com.label.전체', '전체')) },
      ...Array.from(
        new Map<string, Code>(
          (eqclGroups || []).map((item) => [
            item.eqclId,
            { cmnCd: item.eqclId, cmnCdNm: item.eqclId },
          ])
        ).values()
      ),
    ];
  }, [eqclGroups]);

  const optionPrjSpCds = useMemo(() => {
    return (prjSpCds || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.prjSpCd,
          cmnCdNm: cur.prjSpNm,
        },
      ],
      [] as Code[]
    );
  }, [prjSpCds]);

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.elmPrjId,
          cmnCdNm: cur.elmPrjNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjMasterData]);

  useEffect(() => {
    getCommonCodes();
  }, []);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('PRJ_ORG_TP_CD'),
      getCommonCodeNames('PRJ_SP_DIVS_CD'), // 설치구분 (ex. 운송, 기구 등)
      getCommonCodeNames('ISSU_CRTN_CD'), // 발행기준
      getCommonCodeNames('PSG_CRGE_TP_CD'), // 처리담당자유형
      findSetupStep({ useYn: 'Y' }),
      findDisplayProject(),
    ]).then(([prjOrgTpCd, prjSpDivsCd, issuCrtnCd, psgCrgeTpCd, prjSpCd, projects]) => {
      setCode({
        prjOrgTpCd: prjOrgTpCd,
        prjSpDivsCd: prjSpDivsCd,
        issuCrtnCd: issuCrtnCd,
        psgCrgeTpCd: psgCrgeTpCd,
      });
      setPrjSpCds(prjSpCd);
      setPrjMasterData(projects);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(condition);
    }
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleLoadCandidates = useEvent(() => {
    if (!conditionRef.current?.elmPrjId) {
      openMessageBar({
        type: 'warning',
        content: String(t('setup.label.조회 후 등록해 주세요.', '조회 후 등록해 주세요.')),
      });
      return;
    }
    findActivityCandidates(
      conditionRef.current?.elmPrjId,
      conditionRef.current?.orgTpCd || '',
      conditionRef.current?.istlDivsCd || ''
    ).then((result) => {
      if ((result || []).length === 0) {
        openMessageBar({
          type: 'warning',
          content: t(
            'setup.label.가져올 기준 Activity가 없습니다 (모두 설정되었거나 마스터 미정의).',
            '가져올 기준 Activity가 없습니다 (모두 설정되었거나 마스터 미정의).'
          ),
        });
        return;
      }
      if ((eqclGroups || []).length === 0 && conditionRef.current?.orgTpCd) {
        findOrgEqclGroups(conditionRef.current?.orgTpCd).then((eqcl) => setEqclGroups(eqcl));
      }
      const rows = [
        ...(result || []).map((o) => ({
          ...o,
          crudKey: CrudCode.CREATE,
          elmPrjId: conditionRef.current?.elmPrjId,
        })),
        ...rowData,
      ];
      setRowData(rows);
      openMessageBar({
        type: 'confirm',
        content: t(
          `setup.label.기준 Activity {{listCnt}}건을 불러왔습니다 (설비군 대상은 마스터 기준 복사) — 확인 후 [저장]하세요.`,
          {
            defaultValue: `기준 Activity {{listCnt}}건을 불러왔습니다 (설비군 대상은 마스터 기준 복사) — 확인 후 [저장]하세요.`,
            listCnt: (result || []).length.toLocaleString(),
          }
        ),
      });
    });
  });

  const handleGenerateWo = useEvent(() => {
    if (!conditionRef.current?.elmPrjId) {
      openMessageBar({
        type: 'warning',
        content: String(t('setup.label.조회 후 등록해 주세요.', '조회 후 등록해 주세요.')),
      });
      return;
    }
    const items = gridRef.current.rows
      .filter((r) => r.isSelected)
      .map((o) => o.dataItem) as PrjActivity[];
    if (items.length < 1) {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.W/O를 생성할 Activity를 선택하세요.',
          'W/O를 생성할 Activity를 선택하세요.'
        ),
      });
      return;
    }
    if (items.some((o) => o.crudKey === CrudCode.CREATE)) {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.저장되지 않은 Activity가 포함되어 있습니다 — [저장] 후 W/O를 생성하세요.',
          '저장되지 않은 Activity가 포함되어 있습니다 — [저장] 후 W/O를 생성하세요.'
        ),
      });
      return;
    }

    const ids = Array.from(new Set(items.map((o) => o.spActvId)));
    openCommonModal({
      modalType: 'confirm',
      content: t(`setup.label.선택한 Activity {{cnt}}건 기준으로 W/O를 생성하시겠습니까?`, {
        defaultValue: `선택한 Activity {{cnt}}건 기준으로 W/O를 생성하시겠습니까?`,
        cnt: ids.length.toLocaleString(),
      }),
      yesCallback: async () => {
        generatePrjWo(conditionRef.current?.elmPrjId, ids).then((res) => {
          if (res?.successOrNot === 'Y') {
            openMessageBar({
              type: 'confirm',
              content:
                `WO ${res?.data?.woCount}건 + TO-DO ${res?.data?.todoCount}건 생성 완료 (DB 반영)` +
                ((res?.data?.noManagerTypes || []).length > 0)
                  ? ` · 담당자 미등록 유형: ${res?.data?.noManagerTypes.join(
                      ', '
                    )} (해당 WO는 TO-DO 미생성)`
                  : '',
            });
            handleSearch(condition);
          } else {
            openMessageBar({
              type: 'error',
              content:
                res?.data && typeof res?.data === 'string'
                  ? res.data
                  : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          }
        });
      },
    });
  });

  /**
   * 행삭제
   */
  const handleDelRow = () => {
    const selectedRows = gridRef.current?.rows?.filter((r) => r.isSelected);
    if (!selectedRows) return;

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.셋업 단계 별 Activity 목록', '셋업 단계 별 Activity 목록'),
      t('setup.label.셋업 단계 별 Activity 목록', '셋업 단계 별 Activity 목록')
    );
  };

  const handleSave = () => {
    if (!(rowData || []).filter((o) => o.crudKey).length) {
      return;
    }
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        savePrjActivities(rowData).then((res) => {
          openMessageBar({
            type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
            content:
              res?.successOrNot === 'Y'
                ? t('com.label.저장되었습니다.', '저장되었습니다.')
                : res?.data && typeof res?.data === 'string'
                ? res.data
                : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
          if (res?.successOrNot === 'Y') {
            handleSearch(condition);
          }
        });
      },
    });
  };

  const handleSearch = async (params) => {
    setErrors({
      elmPrjId: !params?.elmPrjId,
    });
    conditionRef.current = params;

    if (params?.elmPrjId) {
      // [26.09.01] 해당 프로젝트의 FA 담당자(본사/법인), FA_MANAGER만 수정가능
      let auth = false;
      if (hasRole('ADM') || hasRole('FA_MANAGER')) {
        auth = true;
      } else {
        auth = await checkAvailableProjectManager(params?.elmPrjId, 'HQCOP');
      }
      setHasAuth(auth);
      Promise.all([findPrjActivities(params)]).then(([rows]) => {
        setRowData(rows);
      });
    } else {
      setRowData([]);
    }
    if (params?.orgTpCd) {
      Promise.all([findOrgEqclGroups(params?.orgTpCd)]).then(([eqclGroups]) => {
        setEqclGroups(eqclGroups);
      });
    } else {
      setEqclGroups([]);
    }
  };

  const layoutDefinition = useMemo(() => {
    const mapPrjSpCd = new DataMap(prjSpCds || [], 'prjSpCd', 'prjSpNm');
    mapPrjSpCd.getDisplayValues = (item) => {
      // 선택한 셋업구분값에 따라 셋업단계 콤보박스 필터링 (공통단계 제외)
      return (prjSpCds || [])
        .filter((o) => {
          return o.istlDivsCd === item.istlDivsCd;
        })
        .map((o) => o.prjSpNm || '');
    };
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        align: 'center',
        isReadOnly: true,
        visible: !hasAuth,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        visible: hasAuth,
        cellTemplate: GridStatusCellTemplate,
      },
      ...((eqclGroups || []).length > 0
        ? [
            {
              header: String(t('setup.label.설비군', '설비군')),
              columns: (eqclGroups || [])
                .filter(
                  (o) =>
                    (selectedEqclIds || []).includes(o.eqclId) ||
                    (selectedEqclIds || []).includes('ALL')
                )
                .reduce(
                  (acc, cur) => [
                    ...acc,
                    {
                      binding: `eqclId_${cur.eqclId}`,
                      header: cur.eqclId,
                      minWidth: 43,
                      width: 50,
                      align: 'center',
                      isReadOnly: true,
                      cssClass: 'WijmoCheck',
                      cellTemplate: (params) => {
                        const checked = (params.item?.eqcls || []).includes(cur.eqclId)
                          ? 'checked'
                          : '';
                        const disabled = !hasAuth ? 'disabled' : '';
                        return `<div class="checkbox">
                          <input type="checkbox" id="eqclId" value="${cur.eqclId}" ${checked} ${disabled} />
                          <label /> 
                        </div>
                  `;
                      },
                    },
                  ],
                  [] as any[]
                ),
            },
          ]
        : []),
      {
        binding: 'istlDivsCd',
        header: String(t('setup.label.구분', '구분')),
        align: 'center',
        minWidth: 50,
        width: 82,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.prjSpDivsCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'prjSpCd',
        header: String(t('setup.label.셋업 단계', '셋업 단계')),
        align: 'left',
        minWidth: 40,
        width: '*',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: mapPrjSpCd,
      },
      {
        binding: 'actvNm',
        header: String(t('setup.label.Activity 명', 'Activity 명')),
        align: 'left',
        minWidth: 40,
        width: '*',
      },
      {
        binding: 'issuCrtnCd',
        header: String(t('setup.label.발행기준', '발행기준')),
        align: 'center',
        minWidth: 80,
        width: 100,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.issuCrtnCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'issuPontVal',
        header: String(t('setup.label.D일', 'D일')),
        align: 'center',
        minWidth: 50,
        width: 70,
        isRequired: false,
      },
      {
        binding: 'psgCrgeTpCd',
        header: String(t('setup.label.처리 담당자', '처리 담당자')),
        align: 'center',
        minWidth: 80,
        width: 150,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.psgCrgeTpCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'rltMndYn',
        header: String(t('setup.label.결과필수', '결과필수')),
        align: 'center',
        minWidth: 50,
        width: 100,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(commonYNList || [], 'cmnCd', 'cmnCd'),
      },
    ];
  }, [hasAuth, eqclGroups, selectedEqclIds]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.headersVisibility = hasAuth
        ? HeadersVisibility.All
        : HeadersVisibility.Column;
    }
  }, [hasAuth]);

  const onInitialized = useEvent((grid) => {
    gridRef.current = grid;
    grid.headersVisibility = hasAuth ? HeadersVisibility.All : HeadersVisibility.Column;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (e.target instanceof HTMLInputElement) {
        if (e.target.id === 'eqclId') {
          const arr = [...(item.eqcls || [])];
          if (e.target.checked) {
            if (!arr.includes(e.target.value)) {
              arr.push(e.target.value);
            }
          } else {
            const index = arr.indexOf(e.target.value);
            if (index !== -1) {
              arr.splice(index, 1);
            }
          }
          item.eqcls = arr;
          if (item.crudKey !== CrudCode.CREATE) {
            item.crudKey = CrudCode.UPDATE;
          }
          gridRef.current?.refresh();
        }
      }
    });
  });

  return (
    <>
      <style>
        {`
        /* 체크박스 disable css */ 
        .wj-flexgrid div[wj-part=root] div[wj-part=cells] .wj-cell.WijmoCheck .checkbox input[type=checkbox]:disabled+label {
          opacity: 1;
        }
        .wj-flexgrid div[wj-part=root] div[wj-part=cells] .wj-cell.WijmoCheck .checkbox input[type=checkbox]:disabled {
          cursor: default;
        }
        `}
      </style>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>
                  <span className="dot">{String(t('setup.label.프로젝트', '프로젝트'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster || []}
                  defaultValue={condition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                    const elmPrjTpCd =
                      (prjMasterData || []).find((o) => o.elmPrjId === value)?.elmPrjTpCd || '';
                    handleOnChange('orgTpCd', elmPrjTpCd);
                    setErrors((prev) => ({
                      ...prev,
                      elmPrjId: false,
                    }));
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트를 선택해 주세요.', '프로젝트를 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('setup.label.조직유형', '조직유형'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={code?.prjOrgTpCd || []}
                  defaultValue={condition?.orgTpCd}
                  onChange={(value) => {
                    handleOnChange('orgTpCd', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('setup.label.셋업구분', '셋업구분'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={code?.prjSpDivsCd || []}
                  defaultValue={condition?.istlDivsCd}
                  onChange={(value) => handleOnChange('istlDivsCd', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('setup.label.셋업단계', '셋업단계'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionPrjSpCds || []}
                  defaultValue={condition?.prjSpCd}
                  onChange={(value) => handleOnChange('prjSpCd', value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={defaultCondition}
            setReset={setCondition}
            onSearch={() => handleSearch(condition)}
          />
        </SearchBoxRow>
      </SearchBox>

      {(optionsEqclGroups || []).length > 1 && (
        <SubTitleLayout>
          <ControlBtnGroup>
            <CustomChip
              options={optionsEqclGroups || []}
              defaultValue={selectedEqclIds || []}
              singleSelect={false}
              onChange={(value) => {
                let values = (value || []) as string[];
                if (values.includes('ALL')) {
                  if (selectedEqclIds.includes('ALL') && values.length > 1) {
                    values = values.filter((v) => v !== 'ALL');
                  } else {
                    values = ['ALL'];
                  }
                }
                if (values.length < 1) {
                  values = [...selectedEqclIds];
                }
                setSelectedEqclIds(values);
              }}
            />
          </ControlBtnGroup>
        </SubTitleLayout>
      )}
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.셋업 단계 별 Activity 목록', '셋업 단계 별 Activity 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <>
              <Button
                css={IconButton.button}
                className="reception"
                onClick={handleLoadCandidates}
                disableRipple
              >
                {t('setup.label.기준 Activity 가져오기', '기준 Activity 가져오기')}
              </Button>
              <Button
                css={IconButton.button}
                className="rule"
                onClick={handleGenerateWo}
                disableRipple
              >
                {t('setup.label.W/O 생성', 'W/O 생성')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isReadOnly={!hasAuth}
        height={(optionsEqclGroups || []).length > 1 ? 504 : 581}
        initialized={onInitialized}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={() => handleSave()}>
            {t('com.label.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default SetupProjectActivityPage;
